"use client";

import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MoveRight, Stethoscope, Activity } from "lucide-react";
const employees = [];
export default function MedicalLeadership() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="36442" id="the-caregivers" ref={ref} className={`bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24 text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 font-serif">
            Our Compassionate <span className="text-[var(--primary)]">Caregivers</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed font-sans">
            Our dedicated team of professionals brings world-class medical expertise and community-focused care to every patient. Our compassionate caregivers include:
          </p>
          <ul className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-mono uppercase tracking-wider text-[var(--primary)] list-none">
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-[var(--secondary)] rotate-45" />
              Gibrilla Sannoh (Proprietor and Medical Director)
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-[var(--secondary)] rotate-45" />
              Dr. Victor Conteh (Medical Operations Manager)
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-[var(--secondary)] rotate-45" />
              Dr. Linda Ukwu (Medical staff)
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-[var(--secondary)] rotate-45" />
              Alimatu Koroma, NP (Medical staff)
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-[var(--secondary)] rotate-45" />
              Omolara Amosu, NP (Medical staff)
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-[var(--secondary)] rotate-45" />
              Fatima Turay (Office Manager)
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-[var(--secondary)] rotate-45" />
              Mr. ABU KOROMA (Clinical Aid)
            </li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {employees.map((member, i) => <Dialog key={member.name}>
              <DialogTrigger asChild>
                <div data-index={i} className="group relative cursor-pointer overflow-hidden rounded-3xl bg-card border border-border/60 transition-all duration-500 hover:border-[var(--primary)]/30 hover:shadow-xl hover:shadow-[var(--primary)]/5">
                  <div className="aspect-[3/4] overflow-hidden relative bg-muted">
                    <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent opacity-60 z-10" />
                    <img src={member.image} alt={member.name} className="h-full w-full object-cover transition-all duration-700 grayscale group-hover:grayscale-0 group-hover:scale-105" />
                    
                    <div className="absolute top-4 right-4 z-20 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                      <Badge variant="secondary" className="bg-background/80 backdrop-blur-md border-border/50 text-xs font-mono tracking-wider uppercase text-[var(--primary)]">
                        View Profile
                      </Badge>
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-6 z-20 bg-gradient-to-t from-background/95 via-background/80 to-transparent backdrop-blur-sm pt-12">
                    <h3 className="text-xl font-bold font-serif text-foreground mb-1 group-hover:text-[var(--primary)] transition-colors">
                      {member.name}
                    </h3>
                    <p className="font-mono text-xs text-[var(--chart-2)] uppercase tracking-widest mb-3">
                      {member.role}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 h-0 opacity-0 group-hover:h-auto group-hover:opacity-100 transition-all duration-500 overflow-hidden">
                      {member.credentials.slice(0, 2).map((cred, idx) => <span key={idx} className="text-[10px] font-mono border border-[var(--primary)]/30 text-[var(--primary)] px-2 py-0.5 rounded-full bg-[var(--primary)]/5">
                          {cred}
                        </span>)}
                    </div>
                  </div>
                </div>
              </DialogTrigger>

              <DialogContent className="bg-card border-border sm:max-w-2xl p-0 overflow-hidden gap-0">
                <div className="grid md:grid-cols-2 h-full">
                  <div className="relative h-64 md:h-full min-h-[300px]">
                    <img src={member.image} alt={member.name} className="absolute inset-0 h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent md:bg-gradient-to-r" />
                  </div>
                  
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <DialogHeader className="mb-4 text-left">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-[var(--primary)] text-[var(--primary)] font-mono text-[10px] uppercase">
                          {member.role}
                        </Badge>
                      </div>
                      <DialogTitle className="text-3xl font-bold font-serif mb-1 text-foreground">
                        {member.name}
                      </DialogTitle>
                      <DialogDescription className="text-muted-foreground hidden">
                        Profile details for {member.name}
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6">
                      <p className="text-muted-foreground leading-relaxed font-sans">
                        {member.bio}
                      </p>

                      <div>
                        <h4 className="text-sm font-mono uppercase text-[var(--chart-2)] mb-3 tracking-wider flex items-center gap-2">
                          <Activity className="w-4 h-4" /> Credentials
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {member.credentials.map((cred, idx) => <Badge key={idx} variant="secondary" className="bg-muted/50 hover:bg-muted text-foreground font-sans">
                              {cred}
                            </Badge>)}
                        </div>
                      </div>

                      <div className="pt-4 border-t border-border/50">
                        <Button className="w-full group bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white rounded-full h-12" variant="default">
                          Book Appointment <MoveRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>)}
        </div>
      </div>
    </section>;
}